import { da } from "date-fns/locale";
import {
  AnalyticsPlatform,
  AnalyticsPlatformData,
  FacebookParams,
  InitDefaultParams,
  InitPlatform,
  Report,
} from "../types";
import { addAnalytics } from './store';
import { getUTMParams } from './utils';

const initGa4: InitPlatform = ({ id }) => new Promise((resolve, reject) => {
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;

  const report: Report = (...args) => {
    if (!window.gtag) {
      window.console.error('No gtag');
      return;
    };

    window.gtag(...args);
  }

  // Event listener for successful loading
  script.addEventListener('load', () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
          window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', id, { 'allow_enhanced_conversions': true, ...getUTMParams() });
      resolve(report);
  });
  // Event listener for error in loading the script
  script.addEventListener('error', () => {
      reject(new Error('Failed to load Google Analytics script'));
  });

  // Append the script to the body
  document.body.appendChild(script);
});

const FACEBOOK_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

const getFacebookStub = (): any => window.fbq;

const isFacebookPixelReady = () => typeof getFacebookStub()?.callMethod === 'function';

const isFacebookPixelScriptPresent = () => Array.from(document.getElementsByTagName('script'))
  .some((script) => script.src.includes('connect.facebook.net'));

const ensureFacebookPixel = () => {
  if (isFacebookPixelReady()) return;

  if (!window.fbq) {
    const stub: any = function (this: unknown) {
      // eslint-disable-next-line prefer-rest-params, prefer-spread
      stub.callMethod ? stub.callMethod.apply(stub, arguments) : stub.queue.push(arguments);
    };
    window.fbq = stub;
  }

  const fb: any = getFacebookStub();
  if (!Array.isArray(fb.queue)) fb.queue = [];
  if (!fb.push) fb.push = fb;
  fb.loaded = true;
  fb.version = fb.version || '2.0';
  if (!window._fbq) window._fbq = fb;

  if (isFacebookPixelScriptPresent()) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = FACEBOOK_PIXEL_SRC;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

const initFacebook: InitPlatform<any, FacebookParams> = ({ id, second_id, partner_pixel_id, content_ids, eventref }) => {
  ensureFacebookPixel();

  const report: Report = (...args) => {
    ensureFacebookPixel();

    const fb = getFacebookStub();
    if (!fb) {
      window.console.error('No facebook reporter');
      return;
    }

    fb(...args);
  }

  report('init', id);
  if (partner_pixel_id) {
    report('addPixelId', partner_pixel_id);
  } else if (second_id) {
    report('addPixelId', second_id);
  }
  report('track', 'PageView');
  report('track', 'ViewContent', {
    content_type: 'product',
    eventref: eventref || '',
    content_ids: content_ids || [],
  });

  return report;
}

interface GaParams extends InitDefaultParams {
  name?: string;
}

const initGa: InitPlatform<GaParams> = ({ id, name = 'client' }) => {
  if(!window.ga){
    (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){ // eslint-disable-line
    // @ts-ignore
    (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o), // eslint-disable-line
    m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
    })(window,document,'script','//www.google-analytics.com/analytics.js','ga');
  }

  const _ga = window.ga;

  const createTracker = () => {
    _ga('create', id, {
      'name': name,
      'allowLinker': true
    });
    _ga(name + '.require', 'ec');
  }

  _ga(() => {
    let tracker = ga.getByName(name);
    if (!tracker) {
      createTracker();
      _ga(`${name}.send`, "pageview");
    }
  });

  return (...args) => {
    if (!_ga) {
      window.console.error('No google analytics found');
      return;
    }

    _ga(...args);

    if (!args[1] && args[1] === 'click') {
      _ga(() => {
        ga.remove(name);
        createTracker();
      })
    }
  }
}

const initBing: InitPlatform = ({ id }) => {
  //@ts-ignore
  //NOSONAR
  (function (w, d, t, r, u) { var f, n, i; w[u] = w[u] || [], f = function () { var o = { ti: id, enableAutoSpaTracking: true }; o.q = w[u], w[u] = new UET(o), w[u].push("pageLoad") }, n = d.createElement(t), n.src = r, n.async = 1, n.onload = n.onreadystatechange = function () { var s = this.readyState; s && s !== "loaded" && s !== "complete" || (f(), n.onload = n.onreadystatechange = null) }, i = d.getElementsByTagName(t)[0], i.parentNode.insertBefore(n, i) })(window, document, "script", "//bat.bing.com/bat.js", "uetq");

  return (...args) => {
    if (!window.uetq) {
      window.console.error('No bing analytics found')
      return;
    }

    window.uetq.push(...args)
  }
}

const initAdroll: InitPlatform = ({ id }) => {
  const [adv_id, pix_id] = id.split('/');

  if (!adv_id || !pix_id) {
    window.console.error('Cannot initialize adroll, invalid id: ', id);
    return;
  }

  const script = document.createElement('script');
  script.innerHTML = 'adroll_adv_id = "' + adv_id + '"; adroll_pix_id = "' + pix_id + '"; adroll_version = "2.0";  (function(w, d, e, o, a) { w.__adroll_loaded = true; w.adroll = w.adroll || []; w.adroll.f = [ "setProperties", "identify", "track" ]; var roundtripUrl = "https://s.adroll.com/j/" + adroll_adv_id + "/roundtrip.js"; for (a = 0; a < w.adroll.f.length; a++) { w.adroll[w.adroll.f[a]] = w.adroll[w.adroll.f[a]] || (function(n) { return function() { w.adroll.push([ n, arguments ]) } })(w.adroll.f[a]) }  e = d.createElement("script"); o = d.getElementsByTagName("script")[0]; e.async = 1; e.src = roundtripUrl; o.parentNode.insertBefore(e, o); })(window, document); adroll.track("pageView");';
  document.head.appendChild(script);

  return (...args) => {
    if (!window.adroll) {
      window.console.error('No adroll analytics found');
      return;
    }

    window.adroll.track(...args);
  }
}

const initSegment: InitPlatform = ({ id }) => {
  // @ts-ignore
  !function(){var analytics: any=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey=`${id}`;analytics.SNIPPET_VERSION="4.15.2";
    analytics.load(`${id}`);
    analytics.page();
  }}();

  return (...args) => {
    if (!window.analytics) {
      window.console.error('No Segment analytics found');
    }

    if (args[0] && args[0] === 'pageEvent') {
      window.analytics.page(...args.slice(1));
    } else {
      window.analytics.track(...args);
    }
  }
}

const initSnapchat: InitPlatform = ({ id }) => {
  (function (e, t, n) {
    // @ts-ignore
    if (e.snaptr) return;
     // @ts-ignore
    var a = e.snaptr = function () {
       // @ts-ignore
      a.handleRequest ? a.handleRequest.apply(a, arguments) : a.queue.push(arguments)
    };
     // @ts-ignore
    a.queue = [];
    var s = 'script';
    var r = t.createElement(s);
     // @ts-ignore
    r.async = !0;
     // @ts-ignore
    r.src = n;
    var u = t.getElementsByTagName(s)[0];
    u.parentNode.insertBefore(r, u);
  })(window, document,
    'https://sc-static.net/scevent.min.js');

  window.snaptr('init', id);
  window.snaptr('track', 'PAGE_VIEW');

  return (...args) => {
    if (!window.snaptr) {
      window.console.error('No Snapchat analytics found');
      return;
    }

    window.snaptr(...args);
  }
}

const initTikTok: InitPlatform = ({ id }) => {
  // @ts-ignore
  !function (w, d, t) {
    // @ts-ignore
    w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || []; ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"], ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } }; for (var i = 0; i < ttq.methods.length; i++)ttq.setAndDefer(ttq, ttq.methods[i]); ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)ttq.setAndDefer(e, ttq.methods[n]); return e }, ttq.load = function (e, n) { var i = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = i, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {}; var o = document.createElement("script"); o.type = "text/javascript", o.async = !0, o.src = i + "?sdkid=" + e + "&lib=" + t; var a = document.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a) };
    ttq.load(`${id}`);
    ttq.page();
  }(window, document, 'ttq');

  return (...args) => {
    if (!window.ttq) {
      window.console.error('No TikTok analytics found');
      return;
    }

    window.ttq.track(...args);
  }
}

const initTwitter: InitPlatform = ({ id }) => {
  // @ts-ignore
  !function (e, t, n, s, u, a) {
  // @ts-ignore
  e.twq || (s = e.twq = function () {
  s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
  }, s.version = '1.1', s.queue = [], u = t.createElement(n), u.async = !0, u.src = 'https://static.ads-twitter.com/uwt.js',
  a = t.getElementsByTagName(n)[0], a.parentNode.insertBefore(u, a))
  }(window, document, 'script');
  // @ts-ignore
  twq('config', id);

  return (...args) => {
    if (!window.twq) {
      window.console.error('No Twitter analytics found');
      return;
    }

    window.twq(...args);
  }
}

const triggerPixel = (url: string) => {
  const image = window.document.createElement('img');
    image.src = url;
    image.id = new Date().getTime().toString();
    image.width = 1;
    image.height = 1;
    image.onload = image.onerror = () => {
      window.document.body.removeChild(image);
    }
    window.document.body.appendChild(image);
}

const initTickX: InitPlatform = ({ id }) => {
  const image = window.document.createElement('img');
  image.id = 'tickXpixel_cnt' + new Date().getTime().toString();
  image.src = 'https://widgets.tickxiq.com/js/' + (Date.now()) + '/' + id + '/pixel.js';
  image.width = 1;
  image.height = 1;
  image.onload = image.onerror = () => {
    window.document.body.removeChild(image);
  }
  window.document.body.appendChild(image);
}

const initHive: InitPlatform = ({ id }) => {
  (function(h, i, v, e, s, d, k) {
      h.HiveSDKObject = s;
      h[s] = h[s] || function() {
          (h[s].q = h[s].q || []).push(arguments)
      },
      d = i.createElement(v),
      k = i.getElementsByTagName(v)[0];
      d.async = 1;
      d.id = s;
      d.src = e + '?r=' + parseInt(new Date() / 60000);
      k.parentNode.insertBefore(d, k)
  })(window, document, 'script', 'https://cdn-prod.hive.co/static/js/sdk-loader.js', 'HIVE_SDK');
  HIVE_SDK('init', id);
}

const initTheTradeDesk: InitPlatform<string | string[]> = ({ id, args }) => {
  // Load the TTD script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://js.adsrvr.org/up_loader.3.0.0.js';
  document.head.appendChild(script);
  // Initialize TTD conversion events
  window.ttdConversionEventsLayer = window.ttdConversionEventsLayer || [];
  function ttdConversionEvents() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.ttdConversionEventsLayer.push(arguments);
  }

  ttdConversionEvents("init", {
    advertiserId: id,
    pixelIds: Array.isArray(args) ? args : [args]
  });

  return ttdConversionEvents;
}

const initCustomPixel = () => {
  return ({ id }: { id: string }) => {
    triggerPixel(id);
  }
}

const initDataLayer: InitPlatform = () => {
  return (dataLayer) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataLayer);
  }
}

const initMap: Partial<Record<AnalyticsPlatform, InitPlatform<object>>> = {
  'google-analytics-4': initGa4,
  'google-floodlight': initGa4,
  'google-conversion': initGa4,
  'google-analytics': initGa,
  'google-analytics-sc': initGa,
  'facebook': initFacebook,
  'bing-pixel': initBing,
  'adroll': initAdroll,
  'segment-key': initSegment,
  'snapchat': initSnapchat,
  'tiktok-pixel': initTikTok,
  'twitter': initTwitter,
  'el_custom_pixel_page_view': initCustomPixel,
  'el_custom_pixel_add_to_cart': initCustomPixel,
  'el_custom_pixel_purchase': initCustomPixel,
  'tickx-iq': initTickX,
  'hive': initHive,
  'thetradedesk': initTheTradeDesk,
  'custom-gtm': initDataLayer,
  'ecommerce-manager': initDataLayer,
}

export const initPlatforms = async (platforms: Record<AnalyticsPlatform, AnalyticsPlatformData>) => {
  for (const pName in platforms) {
    const platformName = pName as AnalyticsPlatform;
    const data: AnalyticsPlatformData = platforms[platformName];
    const platformInit = initMap[platformName];

    if (!platformInit) {
      window.console.warn(`Trying to init platform: ${platformName}, but no initializer found!`);
      continue;
    }

    try {
      if (data.measurement_ids) {
        for (const id of data.measurement_ids) {
          try {
            const res = platformInit({ id, args: data.args });

            if (res instanceof Promise) {
              const report = await res;
              addAnalytics(platformName, id, report);
            } else {
              addAnalytics(platformName, id, res);
            }

            if (platformName === 'el_custom_pixel_page_view') {
              triggerPixel(id);
            }
          } catch (error) {
            window.console.error(`Failed to initialize ${platformName} with ID ${id}:`, error);
          }
        }
        continue;
      }

      const res = platformInit(data as never);

      const id = data.id;

      if (res instanceof Promise) {
        const report = await res;
        addAnalytics(platformName, id, report);
      } else {
        addAnalytics(platformName, id, res);
      }

      if (platformName === 'el_custom_pixel_page_view') {
        triggerPixel(id);
      }
    } catch (error) {
      window.console.error(`Failed to initialize ${platformName}:`, error);
    }
  }
}
